import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import apiBaseUrl from "../../utils/config";
import authorizationService from "../Authorization/authorizationService";
import DiagnosisCombinerService from "../Authorization/diagnosisCombinerService";
import TooltipInput from "../../NewUPT/ToolTip/TooltipInput";
import TooltipSelect from "../../NewUPT/ToolTip/TooltipSelect";
import "../../NewUPT/registration/searchPopupDesign.css";
import "./RegisterAuthorization.css";

const patientAuthorizationService = new authorizationService();
const diagnosisCombinerService = new DiagnosisCombinerService();

const emptyForm = {
  patientId: "",
  patientName: "",
  inspt_id: "",
  referredBy: "",
  authCode: "",
  dayStart: "",
  dayEnd: "",
  totalVisits: "",
  visits: "",
  weeklyVisitLimit: "0",
  active: "Yes",
};

const emptyHoldFields = {
  referredBy: false,
  authCode: false,
  dayStart: false,
  dayEnd: false,
  totalVisits: false,
  visits: false,
  active: false,
  procedures: false,
  diagnoses: false,
};

const RegisterAuthorization = () => {
  const location = useLocation();

  const [form, setForm] = useState(emptyForm);
  const [procedureSet, setProcedureSet] = useState([]);
  const [diagnosisSet, setDiagnosisSet] = useState([]);
  const [holdFields, setHoldFields] = useState(emptyHoldFields);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getPreselectedAuthorizationInfoFromUrl = () => {
    const params = new URLSearchParams(location.search);

    const urlPatientId = params.get("patientId") || "";
    const urlPatientName = params.get("patientName") || "";
    const urlInsptId = params.get("insptId") || "";

    return {
      urlPatientId,
      urlPatientName,
      urlInsptId,
    };
  };

  useEffect(() => {
    const { urlPatientId, urlPatientName, urlInsptId } =
      getPreselectedAuthorizationInfoFromUrl();

    if (urlPatientId) {
      setForm((prev) => ({
        ...prev,
        patientId: urlPatientId,
        patientName: urlPatientName || `Patient #${urlPatientId}`,
        inspt_id: urlInsptId || "",
      }));
    }
  }, [location.search]);

  useEffect(() => {
    const handleMessage = (event) => {
      const { inputTarget, value, displayName } = event.data || {};

      if (inputTarget === "patientId") {
        setForm((f) => ({
          ...f,
          patientId: value,
          patientName: displayName || "",
          inspt_id: "",
        }));
      }

      if (inputTarget === "insptId") {
        setForm((f) => ({
          ...f,
          inspt_id: value,
        }));
      }

      if (inputTarget === "procedure") {
        if (!value) return;

        setProcedureSet((prev) => {
          const cleanValue = String(value).trim();
          if (!cleanValue) return prev;
          return prev.includes(cleanValue) ? prev : [...prev, cleanValue];
        });
      }

      if (inputTarget === "diagnosis") {
        if (!value) return;

        setDiagnosisSet((prev) => {
          const cleanValue = String(value).trim();
          if (!cleanValue) return prev;
          return prev.includes(cleanValue) ? prev : [...prev, cleanValue];
        });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const toggleHoldField = (fieldName) => {
    setHoldFields((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const HoldCheckbox = ({ fieldName, label = "Hold" }) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "0.85rem",
        marginTop: "4px",
        marginBottom: "10px",
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={holdFields[fieldName]}
        onChange={() => toggleHoldField(fieldName)}
      />
      {label}
    </label>
  );

  const openPatientPopup = () => {
    const url = `/search-popup?type=Patient&inputTarget=patientId&url=${encodeURIComponent(
      apiBaseUrl + "/patientinfo/search/all"
    )}`;

    window.open(url, "popup", "width=600,height=500");
  };

  const openInsuranceRecordPage = () => {
    if (!form.patientId) {
      alert("Please select a patient first.");
      return;
    }

    const url = `/insurance-record-selector?patientId=${form.patientId}`;
    window.open(url, "popup", "width=700,height=600");
  };

  const openSelectorPage = (type) => {
    const url = `/select-procedure-diagnosis?type=${type}`;
    window.open(url, "_blank", "width=900,height=700");
  };

  const removeProcedure = (idToRemove) => {
    setProcedureSet((prev) => prev.filter((id) => id !== idToRemove));
  };

  const removeDiagnosis = (idToRemove) => {
    setDiagnosisSet((prev) => prev.filter((id) => id !== idToRemove));
  };

  const clearProcedures = () => {
    const confirmed = window.confirm("Clear all selected procedures?");
    if (!confirmed) return;
    setProcedureSet([]);
  };

  const clearDiagnoses = () => {
    const confirmed = window.confirm("Clear all selected diagnoses?");
    if (!confirmed) return;
    setDiagnosisSet([]);
  };

  const handleTotalVisitsChange = (value) => {
    setForm((prev) => ({
      ...prev,
      totalVisits: value,
      visits: holdFields.visits ? prev.visits : value,
    }));
  };

  const buildFormAfterSubmit = (prev) => ({
    ...emptyForm,

    patientId: prev.patientId,
    patientName: prev.patientName,
    inspt_id: prev.inspt_id,

    referredBy: holdFields.referredBy ? prev.referredBy : "",
    authCode: holdFields.authCode ? prev.authCode : "",
    dayStart: holdFields.dayStart ? prev.dayStart : "",
    dayEnd: holdFields.dayEnd ? prev.dayEnd : "",
    totalVisits: holdFields.totalVisits ? prev.totalVisits : "",
    visits: holdFields.visits ? prev.visits : "",
    active: holdFields.active ? prev.active : "Yes",
  });

  const resetForNewAuthorizationSamePatient = () => {
    setForm((prev) => buildFormAfterSubmit(prev));

    setProcedureSet((prev) => (holdFields.procedures ? prev : []));
    setDiagnosisSet((prev) => (holdFields.diagnoses ? prev : []));
  };

  const resetEverything = () => {
    setForm(emptyForm);
    setProcedureSet([]);
    setDiagnosisSet([]);
    setHoldFields(emptyHoldFields);
  };

  const validateForm = () => {
    if (!form.patientId) {
      alert("❗ Please select a patient.");
      return false;
    }

    if (!form.inspt_id) {
      alert("❗ Please select an insurance record.");
      return false;
    }

    if (!form.dayStart || !form.dayEnd) {
      alert("❗ Please select both start and end dates.");
      return false;
    }

    if (form.dayStart > form.dayEnd) {
      alert("❗ Start date cannot be after end date.");
      return false;
    }

    const parsedTotalVisits = parseInt(form.totalVisits, 10);
    const parsedRemainingVisits = parseInt(form.visits, 10);

    if (Number.isNaN(parsedTotalVisits) || parsedTotalVisits < 0) {
      alert("❗ Please enter a valid total authorized visits number.");
      return false;
    }

    if (Number.isNaN(parsedRemainingVisits) || parsedRemainingVisits < 0) {
      alert("❗ Please enter a valid remaining visits number.");
      return false;
    }

    if (parsedRemainingVisits > parsedTotalVisits) {
      alert("❗ Remaining visits cannot be greater than total authorized visits.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const parsedTotalVisits = parseInt(form.totalVisits, 10);
      const parsedRemainingVisits = parseInt(form.visits, 10);

      const authData = {
        patientId: parseInt(form.patientId, 10),
        inspt_id: parseInt(form.inspt_id, 10),
        referredBy: form.referredBy.trim(),
        authCode: form.authCode.trim(),
        dayStart: form.dayStart,
        dayEnd: form.dayEnd,
        totalVisits: parsedTotalVisits,
        visits: parsedRemainingVisits,
        weeklyVisitLimit: parseInt(form.weeklyVisitLimit || "0", 10),
        active: form.active,
      };

      const authResponse =
        await patientAuthorizationService.addPatientAuthorization(authData);

      if (!authResponse || !authResponse.authId) {
        throw new Error("Authorization saved, but no authId was returned.");
      }

      const diagData = {
        authId: authResponse.authId,
        procedureSet: procedureSet.join(","),
        diagnosisSet: diagnosisSet.join(","),
      };

      await diagnosisCombinerService.saveOrUpdate(diagData);

      const keepPatientInfo = window.confirm(
        "✅ Authorization & Diagnosis Registration Successful!\n\nDo you want to keep this patient and insurance selected to add another authorization?"
      );

      if (keepPatientInfo) {
        resetForNewAuthorizationSamePatient();
      } else {
        resetEverything();
      }
    } catch (error) {
      console.error("Registration failed:", error);
      alert("❌ Registration Failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-registration-page">
      <form className="auth-registration-form" onSubmit={handleSubmit}>
        <div className="auth-registration-header">
          <h2>Register Authorization & Diagnosis</h2>
          <p>
            Select the patient and insurance record, enter authorization details,
            then attach procedures and diagnoses.
          </p>
        </div>

        <div className="auth-registration-grid">
          <section className="auth-card">
            <h3>Patient & Insurance</h3>

            <div className="auth-search-row">
              <button
                className="auth-btn auth-btn-primary"
                type="button"
                onClick={openPatientPopup}
              >
                Search Patient
              </button>

              <input
                type="text"
                value={form.patientName}
                readOnly
                placeholder="Selected Patient"
              />
            </div>

            <div className="auth-selected-id">
              Patient ID: {form.patientId || "N/A"}
            </div>

            <div className="auth-search-row">
              <button
                className="auth-btn auth-btn-primary"
                type="button"
                disabled={!form.patientId}
                onClick={openInsuranceRecordPage}
              >
                Select Insurance Record
              </button>

              <input
                type="text"
                value={form.inspt_id}
                readOnly
                placeholder="Selected Insurance Record ID"
              />
            </div>

            <div className="auth-selected-id">
              Insurance Patient ID: {form.inspt_id || "N/A"}
            </div>
          </section>

          <section className="auth-card">
            <h3>Authorization Details</h3>

            <TooltipInput
              type="text"
              id="referredBy"
              placeholder="Enter Referring Doctor"
              tooltip="Doctor or provider who referred the patient."
              value={form.referredBy}
              onChange={(v) => setForm({ ...form, referredBy: v })}
            />
            <HoldCheckbox fieldName="referredBy" />

            <TooltipInput
              type="text"
              id="authCode"
              placeholder="Enter Authorization Code"
              tooltip="Unique authorization code. If no real authorization is needed, use a simple number from 1-99."
              value={form.authCode}
              onChange={(v) => setForm({ ...form, authCode: v })}
            />
            <HoldCheckbox fieldName="authCode" />

            <div className="auth-two-column">
              <div>
                <TooltipInput
                  type="date"
                  id="dayStart"
                  placeholder="Start Date"
                  tooltip="Start date of authorization."
                  value={form.dayStart}
                  onChange={(v) => setForm({ ...form, dayStart: v })}
                />
                <HoldCheckbox fieldName="dayStart" />
              </div>

              <div>
                <TooltipInput
                  type="date"
                  id="dayEnd"
                  placeholder="End Date"
                  tooltip="End date of authorization."
                  value={form.dayEnd}
                  onChange={(v) => setForm({ ...form, dayEnd: v })}
                />
                <HoldCheckbox fieldName="dayEnd" />
              </div>
            </div>
          </section>

          <section className="auth-card">
            <h3>Visits & Status</h3>

            <div className="auth-two-column">
              <div>
                <TooltipInput
                  type="number"
                  id="totalVisits"
                  placeholder="Total Authorized Visits"
                  tooltip="Total approved visits. This number does not count down."
                  value={form.totalVisits}
                  onChange={handleTotalVisitsChange}
                />
                <HoldCheckbox fieldName="totalVisits" />
              </div>

              <div>
                <TooltipInput
                  type="number"
                  id="visits"
                  placeholder="Remaining Visits"
                  tooltip="Remaining visits. For new authorizations, this usually starts equal to total authorized visits and counts down when superbills are created."
                  value={form.visits}
                  onChange={(v) => setForm({ ...form, visits: v })}
                />
                <HoldCheckbox fieldName="visits" />
              </div>
            </div>

            <TooltipSelect
              id="active"
              label="Is the authorization active?"
              value={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
              options={["Yes", "No"]}
            />
            <HoldCheckbox fieldName="active" />
          </section>

          <section className="auth-card">
            <div className="auth-section-title-row">
              <div>
                <h3>Procedures</h3>
                <HoldCheckbox fieldName="procedures" label="Hold procedures" />
              </div>

              <div className="auth-action-group">
                <button
                  className="auth-btn auth-btn-add"
                  type="button"
                  onClick={() => openSelectorPage("procedure")}
                >
                  + Add Procedure
                </button>

                {procedureSet.length > 0 && (
                  <button
                    className="auth-btn auth-btn-clear"
                    type="button"
                    onClick={clearProcedures}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            <div className="auth-selected-items">
              {procedureSet.length === 0 ? (
                <p className="auth-empty-text">No procedures selected.</p>
              ) : (
                procedureSet.map((id) => (
                  <span key={id} className="auth-chip auth-chip-procedure">
                    <span className="auth-chip-text">{id}</span>

                    <button
                      className="auth-chip-remove"
                      type="button"
                      onClick={() => removeProcedure(id)}
                      aria-label={`Remove procedure ${id}`}
                      title={`Remove procedure ${id}`}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
          </section>

          <section className="auth-card">
            <div className="auth-section-title-row">
              <div>
                <h3>Diagnoses</h3>
                <HoldCheckbox fieldName="diagnoses" label="Hold diagnoses" />
              </div>

              <div className="auth-action-group">
                <button
                  className="auth-btn auth-btn-add"
                  type="button"
                  onClick={() => openSelectorPage("diagnosis")}
                >
                  + Add Diagnosis
                </button>

                {diagnosisSet.length > 0 && (
                  <button
                    className="auth-btn auth-btn-clear"
                    type="button"
                    onClick={clearDiagnoses}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            <div className="auth-selected-items">
              {diagnosisSet.length === 0 ? (
                <p className="auth-empty-text">No diagnoses selected.</p>
              ) : (
                diagnosisSet.map((id) => (
                  <span key={id} className="auth-chip auth-chip-diagnosis">
                    <span className="auth-chip-text">{id}</span>

                    <button
                      className="auth-chip-remove"
                      type="button"
                      onClick={() => removeDiagnosis(id)}
                      aria-label={`Remove diagnosis ${id}`}
                      title={`Remove diagnosis ${id}`}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
          </section>

          <section className="auth-card auth-submit-card">
            <h3>Save Authorization</h3>

            <p>
              This will create the authorization first, then save the selected
              procedures and diagnoses under that authorization.
            </p>

            <button
              className="auth-btn auth-btn-submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : "Register Authorization & Diagnosis"}
            </button>
          </section>
        </div>
      </form>
    </div>
  );
};

export default RegisterAuthorization;